import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LuLayoutDashboard,
  LuMegaphone,
  LuMousePointerClick,
  LuShieldOff,
  LuChartBar,
  LuRadar,
  LuListChecks,
  LuBell,
  LuRefreshCw,
  LuFileText,
  LuSettings,
  LuLifeBuoy,
  LuShieldCheck,
} from 'react-icons/lu';

const NAV = [
  { path: '/dashboard', Icon: LuLayoutDashboard, label: 'Dashboard' },
  { path: '/campaigns', Icon: LuMegaphone, label: 'Campaigns' },
  { path: '/clicks', Icon: LuMousePointerClick, label: 'Click Log' },
  { path: '/blocked', Icon: LuShieldOff, label: 'Blocked IPs' },
  { path: '/analytics', Icon: LuChartBar, label: 'Fraud Analytics' },
  {
    Icon: LuRadar, label: 'Detection',
    children: [
      { path: '/rules', Icon: LuListChecks, label: 'Detection Rules' },
      { path: '/notifications', Icon: LuBell, label: 'Notifications' },
    ],
  },
  { path: '/google-ads', Icon: LuRefreshCw, label: 'Google Ads Sync' },
  { path: '/reports', Icon: LuFileText, label: 'Reports' },
  { path: '/settings', Icon: LuSettings, label: 'Settings' },
  { path: '/support', Icon: LuLifeBuoy, label: 'Help & Support' },
];

const navItemBase = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 10px',
  borderRadius: 8,
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 2,
  transition: 'background 0.12s, color 0.12s',
};

export default function Sidebar({ campaigns, activeCampaign, onSelectCampaign, user, onLogout }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const active = campaigns?.find(c => c.id === activeCampaign);

  return (
    <aside style={{
      position: 'fixed', top: 0, left: 0, bottom: 0,
      width: 'var(--sidebar-width)', background: 'var(--bg-secondary)',
      borderRight: '1px solid var(--border)', display: 'flex',
      flexDirection: 'column', zIndex: 100,
    }}>
      {/* Logo */}
      <div style={{ padding: '16px 18px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, background: 'linear-gradient(135deg, #0969da, #08a86b)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}><LuShieldCheck size={16} /></div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>ClickSaver</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Fraud Protection</div>
          </div>
        </div>
      </div>

      {/* Campaign selector */}
      <div style={{ padding: '0 12px 12px', position: 'relative' }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 6, paddingLeft: 4 }}>
          Active campaign
        </div>
        <button
          onClick={() => setDropdownOpen(o => !o)}
          style={{
            width: '100%', background: 'var(--bg-hover)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
            transition: 'background 0.12s',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
            {active ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {active.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--accent-green)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-green)' }} />
                  {active.domain}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {campaigns?.length === 0 ? 'No campaigns yet' : 'Select campaign...'}
              </div>
            )}
          </div>
          <span style={{ color: 'var(--text-muted)', fontSize: 10, flexShrink: 0 }}>{dropdownOpen ? '▲' : '▼'}</span>
        </button>

        {dropdownOpen && campaigns?.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 12, right: 12, zIndex: 200,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
          }}>
            {campaigns.map(c => (
              <button
                key={c.id}
                onClick={() => { onSelectCampaign(c.id); setDropdownOpen(false); }}
                style={{
                  width: '100%', background: c.id === activeCampaign ? 'var(--bg-hover)' : 'transparent',
                  border: 'none', padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
                  borderBottom: '1px solid var(--border)', display: 'block',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 500, color: c.id === activeCampaign ? 'var(--accent-blue)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.id === activeCampaign ? '✓ ' : ''}{c.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.domain}</div>
              </button>
            ))}
            <NavLink
              to="/campaigns"
              onClick={() => setDropdownOpen(false)}
              style={{ display: 'block', padding: '10px 12px', fontSize: 12, color: 'var(--accent-blue)', textDecoration: 'none', textAlign: 'center', background: 'var(--bg-subtle)', fontWeight: 500 }}
            >
              + Manage campaigns
            </NavLink>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '4px 10px', overflowY: 'auto' }}>
        {NAV.map((item) => {
          if (item.children) {
            const isGroupActive = item.children.some(c => location.pathname === c.path);
            return (
              <div key={item.label} style={{ marginBottom: 2 }}>
                <div style={{
                  ...navItemBase,
                  color: isGroupActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                  cursor: 'default',
                }}>
                  <item.Icon size={16} style={{ flexShrink: 0 }} />{item.label}
                </div>
                {item.children.map(child => (
                  <NavLink
                    key={child.path}
                    to={child.path}
                    onClick={() => setDropdownOpen(false)}
                    style={({ isActive }) => ({
                      ...navItemBase,
                      padding: '6px 10px 6px 36px',
                      fontSize: 12.5,
                      background: isActive ? 'var(--bg-hover)' : 'transparent',
                      color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontWeight: isActive ? 600 : 500,
                    })}
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setDropdownOpen(false)}
              style={({ isActive }) => ({
                ...navItemBase,
                background: isActive ? 'var(--bg-hover)' : 'transparent',
                color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: isActive ? 600 : 500,
              })}
            >
              <item.Icon size={16} style={{ flexShrink: 0 }} />{item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* User */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
        {user && (
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #0969da, #08a86b)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600, fontSize: 12, flexShrink: 0 }}>
              {(user.name || user.email || '?').trim().charAt(0).toUpperCase()}
            </div>
            <div style={{ overflow: 'hidden', minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name || user.email}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
            </div>
          </div>
        )}
        <button onClick={onLogout} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'center', padding: '7px 12px', fontSize: 13 }}>
          Sign Out
        </button>
      </div>
    </aside>
  );
}
